import { FlipHorizontal2, GripVertical, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SectionAxis, SectionProfile, SectionReading, SectionState } from '../engine/types';
import { CutCanvas } from './CutCanvas';
import { FloatingPanel } from './FloatingPanel';

/**
 * The cut, on the left under the view buttons while it is on, and movable.
 *
 * This is the one thing a flat PDF cannot fake: the dentist drags the cut
 * through the tooth and sees the crown wall, the cement space and the margin
 * on both sides, rather than taking the lab's word for the fit.
 *
 * Beneath the slider, the cut in numbers - what the desktop's Cut View reads
 * off the same plane: the thinnest crown wall, the cement space over the
 * fitting surface, the gap to the neighbours on a cut along the arch, and the
 * cut face itself. See lib/geometry/section.ts for how each is measured.
 */
const AXES: { value: SectionAxis; label: string; hint: string }[] = [
  { value: 'buccolingual', label: 'Cheek → tongue', hint: 'Both walls and both margins' },
  { value: 'mesiodistal', label: 'Along the arch', hint: 'The contacts with neighbours' },
];

interface SectionControlProps {
  section: SectionState;
  teeth: string[];
  offsetMm: number;
  /** The cut's measurements; null while they are being read. */
  reading: SectionReading | null;
  /** The material's minimum wall, which the wall reading is judged against. */
  minThickness: number;
  /** The cut drawn flat, NuCAD's Cut View way. */
  profile: SectionProfile | null;
  onChange: (section: SectionState) => void;
}

const mm = (value: number) => `${value < 0 ? '−' : ''}${Math.abs(value).toFixed(2)} mm`;

function Reading({
  label,
  value,
  note,
  concern = false,
}: {
  label: string;
  value: string;
  note?: string;
  concern?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[10.5px] font-medium tracking-wide text-neutral-500 uppercase">
        {label}
      </dt>
      <dd
        className={cn(
          'font-mono text-[12.5px] font-medium text-neutral-800 tabular-nums dark:text-neutral-100',
          concern && 'text-danger-500 dark:text-danger-500',
        )}
      >
        {value}
      </dd>
      {note && <dd className="text-[10.5px] leading-snug text-neutral-500">{note}</dd>}
    </div>
  );
}

function Readings({
  reading,
  axis,
  minThickness,
}: {
  reading: SectionReading | null;
  axis: SectionAxis;
  minThickness: number;
}) {
  if (!reading) {
    return <p className="text-[11.5px] text-neutral-500">Measuring the cut…</p>;
  }
  if (reading.outline === 0) {
    return <p className="text-[11.5px] text-neutral-500">The cut misses the crown here.</p>;
  }
  const { wall, gap } = reading;
  return (
    <>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
        <Reading
          label="Thinnest wall"
          value={wall === null ? '—' : mm(wall)}
          note={`Material minimum ${mm(minThickness)}`}
          concern={wall !== null && wall < minThickness}
        />
        <Reading
          label="Cement gap"
          value={
            gap
              ? gap.min.toFixed(2) === gap.max.toFixed(2)
                ? mm(gap.min)
                : `${gap.min.toFixed(2)}–${mm(gap.max)}`
              : '—'
          }
          note={
            gap
              ? gap.min < 0
                ? 'Prep comes through the crown'
                : `Average ${mm(gap.mean)}`
              : reading.hasJaw
                ? 'No prep within 1 mm in this cut'
                : 'No jaw scan in this design'
          }
          concern={gap !== null && gap.min < 0}
        />
        {axis === 'mesiodistal' && (
          <Reading
            label="Neighbour"
            value={reading.neighbour === null ? '—' : mm(reading.neighbour)}
            note={
              reading.neighbour === null
                ? 'None within 2 mm'
                : reading.neighbour < 0.05
                  ? 'In contact'
                  : 'Closest approach'
            }
          />
        )}
        <Reading
          label="Cut face"
          value={`${reading.area.toFixed(1)} mm²`}
          note={`Outline ${reading.outline.toFixed(1)} mm`}
        />
      </dl>
      {reading.layers.length > 0 && (
        <p
          className="mt-2 truncate text-[11px] text-neutral-500"
          title={reading.layers.join(' · ')}
        >
          Cuts through {reading.layers.join(' · ')}
        </p>
      )}
    </>
  );
}

export function SectionControl({
  section,
  teeth,
  offsetMm,
  reading,
  minThickness,
  profile,
  onChange,
}: SectionControlProps) {
  return (
    <FloatingPanel className="w-[min(26rem,calc(100vw-1.5rem))] px-3 py-2.5">
      {/* The header moves the panel (DraggablePanel); a double-click puts it back. */}
      <div
        data-drag-handle
        title="Drag to move - double-click to put it back"
        className="-mx-1 flex cursor-grab touch-none flex-wrap items-center gap-2 rounded-md px-1 select-none active:cursor-grabbing"
      >
        <GripVertical aria-hidden className="-mr-1 size-4 text-neutral-400" />
        <span className="text-[13px] font-semibold text-neutral-800 dark:text-neutral-100">
          Cut view
        </span>

        {teeth.length > 1 && (
          <div
            role="radiogroup"
            aria-label="Tooth to cut"
            className="flex gap-0.5 rounded-md bg-neutral-900/5 p-0.5 dark:bg-white/10"
          >
            {teeth.map((tooth) => (
              <button
                key={tooth}
                type="button"
                role="radio"
                aria-checked={section.tooth === tooth}
                onClick={() => {
                  onChange({ ...section, tooth, position: 0 });
                }}
                className={cn(
                  'h-6 rounded px-2 font-mono text-[11.5px] font-semibold',
                  section.tooth === tooth
                    ? 'bg-white shadow-sm dark:bg-neutral-800'
                    : 'text-neutral-600 dark:text-neutral-300',
                )}
              >
                {tooth}
              </button>
            ))}
          </div>
        )}

        <div
          role="radiogroup"
          aria-label="Direction of the cut"
          className="flex gap-0.5 rounded-md bg-neutral-900/5 p-0.5 dark:bg-white/10"
        >
          {AXES.map((axis) => (
            <button
              key={axis.value}
              type="button"
              role="radio"
              title={axis.hint}
              aria-checked={section.axis === axis.value}
              onClick={() => {
                onChange({ ...section, axis: axis.value, position: 0 });
              }}
              className={cn(
                'h-6 rounded px-2 text-[11.5px] font-medium',
                section.axis === axis.value
                  ? 'bg-white shadow-sm dark:bg-neutral-800'
                  : 'text-neutral-600 dark:text-neutral-300',
              )}
            >
              {axis.label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            aria-label="Cut away the other side"
            title="Cut away the other side"
            onClick={() => {
              onChange({ ...section, flipped: !section.flipped });
            }}
            className="rounded-md p-1 text-neutral-500 hover:bg-neutral-900/5 hover:text-neutral-900 dark:hover:bg-white/10 dark:hover:text-white"
          >
            <FlipHorizontal2 className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Close the section"
            onClick={() => {
              onChange({ ...section, enabled: false });
            }}
            className="rounded-md p-1 text-neutral-500 hover:bg-neutral-900/5 hover:text-neutral-900 dark:hover:bg-white/10 dark:hover:text-white"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      {/* NuCAD's Cut View: the cut follows the cursor over the model; a click holds it. */}
      <div className="mt-2">
        <CutCanvas profile={profile} />
        <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-neutral-500">
          <span>
            {section.locked
              ? 'Held - click the model to follow the cursor again'
              : 'Move over the model to cut - click to hold'}
          </span>
          <button
            type="button"
            onClick={() => {
              onChange({ ...section, locked: !section.locked });
            }}
            className="shrink-0 rounded-md px-2 py-0.5 font-medium text-primary hover:bg-neutral-900/5 dark:hover:bg-white/10"
          >
            {section.locked ? 'Follow' : 'Hold'}
          </button>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="range"
          min={-100}
          max={100}
          value={Math.round(section.position * 100)}
          aria-label="Position of the cut"
          onChange={(event) => {
            onChange({ ...section, position: Number(event.target.value) / 100 });
          }}
          className="stage-range stage-range-accent flex-1"
        />
        <span className="w-20 text-right font-mono text-[12px] font-medium text-neutral-700 tabular-nums dark:text-neutral-200">
          {offsetMm >= 0 ? '+' : '−'}
          {Math.abs(offsetMm).toFixed(2)} mm
        </span>
      </div>

      <div className="mt-2 border-t border-neutral-900/5 pt-2 dark:border-white/10">
        <Readings reading={reading} axis={section.axis} minThickness={minThickness} />
      </div>
    </FloatingPanel>
  );
}
